/**
 * A section's setup gear, carrying its last refresh's outcome — "Refreshing…",
 * "Refresh failed", "N warnings" — until the next refresh replaces it
 * (shipcast-o7j Q4). Shared by the Deliverables and Throughput sections.
 */

import { AlertTriangle, Loader2, Settings } from 'lucide-react';
import { Button, cn } from '@lean-agility/design-system/react';

import { refreshAttention, type TeamRefresh } from './teamRefresh';

/** The gear, with the last refresh's outcome beside it until the next one. */
export function SetupButton({ refresh, label, onClick }: { refresh: TeamRefresh; label: string; onClick: () => void }) {
  const attention = refreshAttention(refresh.latest);
  const warnings = refresh.latest?.warnings.length ?? 0;
  const status = refresh.running
    ? 'Refreshing…'
    : attention === 'error'
      ? 'Refresh failed'
      : attention === 'warnings'
        ? `${warnings} warning${warnings === 1 ? '' : 's'}`
        : null;

  // The gear never changes: replacing it with a warning sign hid where the
  // settings live (shipcast-o7j.10). The outcome carries its own icon instead.
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      title={label}
      onClick={onClick}
      className="h-7 gap-1 px-1.5 text-xs"
    >
      <Settings className="size-4" aria-hidden="true" />
      <span className="sr-only">{label}</span>
      {status && (
        <span
          className={cn(
            'inline-flex items-center gap-1',
            attention === 'error' && !refresh.running && 'text-feedback-error',
            attention === 'warnings' && !refresh.running && 'text-feedback-warning',
          )}
        >
          {refresh.running ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <AlertTriangle className="size-3.5" aria-hidden="true" />
          )}
          {status}
        </span>
      )}
    </Button>
  );
}
